/** Id CRM del presupuesto cuando la fila es `PRESUPUESTO_<IdPresupuesto>`. */
export function parseIdPresupuestoFromRowId(rowId) {
  if (rowId == null) return null;
  const m = String(rowId).match(/^PRESUPUESTO_(\d+)$/i);
  return m ? Number(m[1]) : null;
}

export function parseIdPresupuestoA3PedidoFromRowId(rowId) {
  if (rowId == null) return null;
  const s = String(rowId);
  let m = s.match(/^D_(\d+)$/i);
  if (m) return Number(m[1]);
  m = s.match(/^PEDIDO_(\d+)$/i);
  return m ? Number(m[1]) : null;
}

/**
 * Debe coincidir con `getRowId` del DataGrid.
 * Prioridad: id explícito → pedido A3 → presupuesto CRM → producto → muestra → entityKey.
 */
export function getCriticalPathDataGridRowId(row) {
  if (!row || typeof row !== "object") return "";
  if (row.id != null && String(row.id).trim() !== "") {
    return String(row.id);
  }
  if (
    row.IdPresupuestoA3Pedido != null &&
    Number.isFinite(Number(row.IdPresupuestoA3Pedido))
  ) {
    return `D_${Number(row.IdPresupuestoA3Pedido)}`;
  }
  if (
    row.idPresupuestoCRM != null &&
    Number.isFinite(Number(row.idPresupuestoCRM))
  ) {
    return `PRESUPUESTO_${Number(row.idPresupuestoCRM)}`;
  }
  const pid = row.productId ?? row.ProductId;
  if (pid !== null && pid !== undefined && String(pid).trim() !== "") {
    return `P_${Number(pid)}`;
  }
  const mid = row.idMuestra ?? row.ID_MUESTRA;
  if (mid !== null && mid !== undefined && String(mid).trim() !== "") {
    return `M_${Number(mid)}`;
  }
  return row.entityKey != null ? String(row.entityKey) : "";
}

/** Clave D_/P_/M_ para cierre CP y badges de notas (alineada con BACK). */
export function getClosedEntityKey(rowOrProductId, idMuestra = null, idPedido = null) {
  if (rowOrProductId && typeof rowOrProductId === "object") {
    return getClosedEntityKey(
      rowOrProductId.ProductId,
      rowOrProductId.ID_MUESTRA,
      rowOrProductId.IdPresupuestoA3Pedido,
    );
  }
  if (idPedido != null && Number.isFinite(Number(idPedido))) {
    return `D_${Number(idPedido)}`;
  }
  if (
    rowOrProductId !== null &&
    rowOrProductId !== undefined &&
    String(rowOrProductId).trim() !== ""
  ) {
    return `P_${Number(rowOrProductId)}`;
  }
  if (idMuestra != null && idMuestra !== undefined && String(idMuestra).trim() !== "") {
    return `M_${Number(idMuestra)}`;
  }
  return "";
}

export function prodRowsExcludingMuestras(prod) {
  return (Array.isArray(prod) ? prod : []).filter((r) => r?.ID_MUESTRA == null);
}

/** Marca filas con logística legacy (sin _fromLogisticaEnvio). */
export function withLegacyLogisticsFlag(rows) {
  return (Array.isArray(rows) ? rows : []).map((r) => {
    const hasLegacyLogistics =
      Array.isArray(r?.logistics) &&
      r.logistics.some((entry) => !Boolean(entry?._fromLogisticaEnvio));
    return { ...r, HasLegacyLogistics: hasLegacyLogistics };
  });
}

/** Envíos ProductionLogistics editables en el drawer (excluye logística de pedidos nuevos). */
export function extractLegacyLogistics(row) {
  return Array.isArray(row?.logistics)
    ? row.logistics.filter((entry) => !Boolean(entry?._fromLogisticaEnvio))
    : [];
}

/** PO Date del primer envío legacy (si existe). */
export function getLegacyPoDateFromRow(row) {
  for (const entry of extractLegacyLogistics(row)) {
    const po = entry?.PODate;
    if (po != null && String(po).trim() !== "") return po;
  }
  return null;
}

export function resolveCpIdPresupuestoFromRow(row) {
  if (!row) return null;
  if (
    row.idPresupuestoCRM != null &&
    Number.isFinite(Number(row.idPresupuestoCRM))
  ) {
    return Number(row.idPresupuestoCRM);
  }
  return parseIdPresupuestoFromRowId(row.id);
}

/** Empareja un bloque del hub NotasResumen con la fila del CP horizontal. */
export function findCpRowForHubBlock(block, rows) {
  if (!block || !Array.isArray(rows)) return null;
  if (block.idMuestra) return null;

  if (block.idPresupuestoA3Pedido) {
    return (
      rows.find(
        (r) =>
          Number(r.IdPresupuestoA3Pedido) ===
          Number(block.idPresupuestoA3Pedido),
      ) ?? null
    );
  }

  const idPresupuesto = Number(block.productId);
  if (Number.isFinite(idPresupuesto)) {
    const byPresupuesto =
      rows.find(
        (r) => resolveCpIdPresupuestoFromRow(r) === idPresupuesto,
      ) ??
      rows.find((r) => Number(r.idPresupuestoCRM) === idPresupuesto) ??
      rows.find((r) => Number(r.ProductId) === idPresupuesto) ??
      null;
    if (byPresupuesto) return byPresupuesto;
  }

  if (block.entityKey?.startsWith("P_")) {
    const hubPresupuestoId = Number(String(block.entityKey).slice(2));
    if (Number.isFinite(hubPresupuestoId)) {
      return (
        rows.find((r) => Number(r.idPresupuestoCRM) === hubPresupuestoId) ??
        rows.find(
          (r) => resolveCpIdPresupuestoFromRow(r) === hubPresupuestoId,
        ) ??
        rows.find(
          (r) => parseIdPresupuestoFromRowId(r.id) === hubPresupuestoId,
        ) ??
        null
      );
    }
  }

  return null;
}
